import { ICardPaymentBrickPayer, ICardPaymentFormData } from '@mercadopago/sdk-react/bricks/cardPayment/type';
import axios from 'axios';
import { AUTHSTORAGE_KEY } from 'contexts/useAuth';
import { FormCardCreditCard } from 'layouts/charge/PagSeguroCard';
import { ChargePublic } from 'layouts/charge/types/ChargeTypes';
import { Charge, InstantQrCode } from 'types/Charge';
import { ConsultCep } from 'types/ConsultCep';
import { ConsultCnpj } from 'types/ConsultCnpj';
import { Movement } from 'types/Movement';
import { DashboardResponse } from 'types/dashboard';
import { CreateHolderForm } from 'views/access/insurances/components/CreateHolder';
import { Withdrawal } from 'views/access/withdrawals';
import { BilletResponse, ConsultPixKey, ShowQrCode } from 'views/access/withdrawals/types/QRcode/qrcode.type';
import { RecurrenceResponse } from './types/Recurrence.entity';
import { RecurrencyClientPayments } from './types/RecurrencyPayments.entity';
import { CardList } from './types/cards.type';
import { CustomerInfo } from './types/customer';
import { Dispute } from './types/dispute';
import { InstallmentsConfig } from './types/installmensts.config';
import { Onboarding } from './types/onboarding.type';
import { RecurrencyLink, RecurrencyLinkItemGet } from './types/recurrency-link';

export interface RechargeFormCardCreditCard {
  cardNumber: string;
  cardExpiration: string;
  cardCvv: string;
  cardHolderName: string;
  cardHolderCpf: string;
  installments: number;
  amount: number;
  pagSeguro3dsId?: string;
  method: 'CREDIT_CARD' | 'DEBIT_CARD';
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Set authorization header if token exists
const token = localStorage.getItem('Authorization');
if (token) {
  api.defaults.headers.Authorization = 'Bearer ' + token;
}

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const baseUrl = error.config.url;
    const routesIgnore = ['/customer', '/auth'];
    if (error.response?.status === 401 && !routesIgnore.some((route) => baseUrl.includes(route))) {
      localStorage.removeItem(AUTHSTORAGE_KEY);
      dispatchEvent(new CustomEvent('auth:logout'));
    }
    return Promise.reject(error);
  },
);

// Auto-refresh contract status when the backend returns CONTRACT_REQUIRED.
// The ContractsContext will react via react-query refetch on focus, but
// dispatching a custom event lets the modal open immediately on the same tick.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const code = err?.response?.data?.code;
    if (code === 'CONTRACT_REQUIRED') {
      // Notify ContractsContext to re-poll status (it has a window-focus listener,
      // but a custom event is more deterministic)
      try {
        window.dispatchEvent(new CustomEvent('contracts:re-check'));
      } catch {
        // ignore
      }
    }
    return Promise.reject(err);
  },
);

interface Orders {
  id: number;
  customerName: string;
  providerName: string;
  status: string;
  serviceType: string;
  amount: number;
  on: Date;
}

export const validateUserCode = async (code: string) => {
  return api.post<{ id: string }>('customers/validateCode', { code }).then((res) => res.data);
};

export const registerDevice = async (deviceId: string) => {
  return api.post('customers/device', { deviceId }).then((res) => res.data);
};

export const loginCustomerService = async (
  email: string,
  password: string,
  captchaKey: string | undefined,
  token: string | undefined,
  turnstileToken?: string,
) => {
  return api
    .post('/auth/customer', { email, password, captchaKey, turnstileToken, twoFa: token })
    .then((res) => res.data);
};

export const deleteWithdrawal = (id: string) => {
  return api.delete(`/withdrawals/${id}`);
};

export const updateCustomerService = () => {
  return api.get('/auth/update').then((res) => res.data);
};

export const getAccountsAccess = async () => {
  return api
    .get<{ customerAccess: { id: string; name: string; email: string; accountNumber: string } }[]>('/customers/access')
    .then((res) => res.data);
};

export const changeUserAccount = async (customerIdToAccess: string) => {
  return api.post('/auth/customer/change-user', { customerIdToAccess }).then((res) => res.data);
};

export const getAllowCardRecharge = async () => {
  return api.get<boolean>('/charges/allow-card-recharge').then((res) => res.data);
};

export const findClientById = async (id: string) => {
  return api
    .get<{
      id: string;
      name: string;
      document: string;
      email: string;
      phone: string;
    }>(`/clients/${id}`)
    .then((res) => res.data);
};

export const createClient = async (data: { email: string; phone: string; name: string; document: string }) => {
  return api.post(`clients`, data).then((res) => res.data);
};

export const editClient = async (
  id: string,
  data: {
    email: string;
    phone: string;
    name: string;
    document: string;
  },
) => {
  return api.put(`clients/${id}`, data).then((res) => res.data);
};

export const deleteClient = async (id: string) => {
  return api.delete(`clients/${id}`).then((res) => res.data);
};

export const realizarLevantamentoApi = async (data: { recurrencyId: string; amount: number }) => {
  return api.post(`recurrences/${data.recurrencyId}/withdraw`, data).then((res) => res.data);
};

export const createRecurrency = async (data: { amount: string; description: string }) => {
  return api.post(`recurrences`, data).then((res) => res.data);
};

export const deleteRecurrency = async (id: string) => {
  return api.delete(`recurrences/${id}`).then((res) => res.data);
};

export const findClientPayments = async (id: string) => {
  return api.get<RecurrencyClientPayments>(`recurrences/payments/${id}`).then((res) => res.data);
};

export interface RecurrenceClientsSummary {
  activeClients: number;
  activePlans: number;
  overdueClients: number;
  upcomingClients: number;
  plansWithoutWallet: number;
  pendingAmount: number;
  paidAmount: number;
}

export const findRecurrenceClientsSummary = async () => {
  return api.get<RecurrenceClientsSummary>(`recurrences/clients/summary`).then((res) => res.data);
};

export const removeClientFromRecurrence = async (id: string) => {
  return api.delete(`recurrences/clients/${id}`).then((res) => res.data);
};

export const updateRecurrenceClient = async (
  id: string,
  data: { initDate?: string; endDate?: string; nextDate?: string; lastDate?: string; period?: string },
) => {
  return api.put(`recurrences/clients/${id}`, data).then((res) => res.data);
};

export const createNewRecurrencePaymentRequest = async ({
  id,
  withoutCharge,
}: {
  id: string;
  withoutCharge: boolean;
}) => {
  return api.post(`recurrences/payments/${id}/charge`, { withoutCharge }).then((res) => res.data);
};
export const sendMailRecurrencePaymentRequest = async (id: string) => {
  return api.post(`recurrences/generated/${id}/mail`).then((res) => res.data);
};
export const doneRecurrencePayment = async (id: string) => {
  return api.put(`recurrences/generated/${id}/done`).then((res) => res.data);
};

export const cancelRecurrencePayment = async (id: string) => {
  return api.delete(`recurrences/generated/${id}`).then((res) => res.data);
};

export const newChargeForGeneratedRecurrence = async (id: string) => {
  return api.put(`recurrences/generated/${id}`).then((res) => res.data);
};

export const updateRecurrencePaymentDueDate = async ({
  id,
  data,
}: {
  id: string;
  data: { dueDate: string };
}) => {
  return api.put(`recurrences/generated/${id}/due-date`, data).then((res) => res.data);
};

export const linkWalletToRecurrencyApi = async ({ id, walletId }: { id: string; walletId: string }) => {
  return api.put(`recurrences/${id}/wallet/${walletId}`).then((res) => res.data);
};

export const findAllWallets = async (filter: { limit: number; page: number; filter: string }) => {
  return api
    .get(`wallets`, {
      params: filter,
    })
    .then((res) => res.data);
};

export const addClientToRecurrence = async (
  recurrenceId: string,
  data: {
    clientId: string;
    initDate: string;
    period: string;
    endDate: string;
    generateAllUpfront?: boolean;
  },
) => {
  return api.post(`recurrences/${recurrenceId}/add`, data).then((res) => res.data);
};

export const updateUser = (
  id: string,
  data: {
    email: string;
    password: string;
    name: string;
    companyId: string;
  },
) => {
  return api.put(`users/${id}`, data).then((res) => res.data);
};

export const deleteCustomerTaxes = (customerId: string, taxId: string) => {
  return api.delete(`customers/${customerId}/taxes/${taxId}`).then((res) => res.data);
};

export const getMovement = (id: string) => {
  return api.get<Movement>(`movements/${id}`).then((res) => res.data);
};

export const getRecurrence = async (id: string) => {
  return api.get<RecurrenceResponse>(`recurrences/${id}`).then((res) => res.data);
};

export const deleteTax = (id: string) => {
  return api.delete(`taxes/${id}`).then((res) => res.data);
};
export const ativeUserService = (id: string, ative: boolean) => {
  return api.put(`users/${id}/ative`, { ative }).then((res) => res.data);
};

export interface Customer {
  id: string;
  name: string;
  document_number: string;
  email: string;
  status: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface CustomerListResponse {
  pages: number;
  registers: Customer[];
}

export const findAllParticipants = ({
  limit = 20,
  filter = '',
  page = 0,
}: {
  limit?: number;
  page?: number;
  filter?: string;
}) => {
  return api
    .get<CustomerListResponse>('withdrawals/participants', {
      params: {
        limit,
        page,
        filter,
      },
    })
    .then((res) => res.data);
};

export const createWithdrawal = (authorizationId: string, data: any) => {
  return api
    .post<any>('withdrawals', data, {
      headers: {
        'x-security-code': authorizationId,
      },
    })
    .then((res) => res.data);
};

export const createWithdrawalQRCode = async (
  authorizationId: string,
  data: {
    amount: number;
    description: string;
    qrcode: string;
  },
) => {
  return api
    .post<any>('withdrawals/qrcode', data, {
      headers: {
        'x-security-code': authorizationId,
      },
    })
    .then((res) => res.data);
};
export const createWithdrawalAntecipate = (data: any) => {
  return api.post<any>('withdrawals/antecipate', data).then((res) => res.data);
};

export const createTransfer = (authorizationId: string, data: any) => {
  return api
    .post<any>('withdrawals/transfer', data, {
      headers: {
        'x-security-code': authorizationId,
      },
    })
    .then((res) => res.data);
};

export const createCharge = async (data: {
  amount: number;
  description: string;
  document: string;
  name: string;
  feeType: 'FROM_PAYER' | 'FROM_CUSTOMER';
  intermediary: boolean;
  releaseInDays: number;
  maxInstallments: number;
}) => {
  return api.post<any>('charges', data).then((res) => res.data);
};

export const validateQRCode = async (qrcode: string) => {
  return api.post<ShowQrCode>('withdrawals/validate', { qrcode }).then((res) => res.data);
};
export const validateBillet = async (billet: string) => {
  return api.post<BilletResponse>('withdrawals/validate-billet', { billet }).then((res) => res.data);
};

export const createBilletPayment = async (data: { billetId: string; amount: number; twoFaId: string }) => {
  return api
    .post<BilletResponse>(
      'withdrawals/create-billet-payment',
      {
        billetId: data.billetId,
        amount: data.amount,
      },
      {
        headers: {
          'x-security-code': data.twoFaId,
        },
      },
    )
    .then((res) => res.data);
};

export const validatePixKey = async (key: string) => {
  return api.post<ConsultPixKey>('withdrawals/validate-pix', { key }).then((res) => res.data);
};

export const transferAmountAnotherCustomer = (customerId: string, data: any) => {
  return api.post<any>(`customers/${customerId}/transfer`, data).then((res) => res.data);
};

export const findAllClientsFilter = ({ filter, limit }: { limit: number; filter: string }) => {
  return api
    .get('customers/simple', {
      params: {
        limit,
        filter,
      },
    })
    .then((res) => res.data);
};

export const findCnpj = (cnpj: string) => {
  return api.get<ConsultCnpj>(`customers/cnpj/${cnpj}`).then((res) => res.data);
};

export const findCep = (cep: string) => {
  return api.get<ConsultCep>(`ceps/${cep}`).then((res) => res.data);
};

export const changePasswordCustomer = (customerId: string, password: string, currentPassword: string) => {
  return api
    .put(`customers/${customerId}/changePassword`, {
      password,
      currentPassword,
    })
    .then((res) => res.data);
};

export const findAllTerminals = (filters: {
  limit: number;
  page: number;
  filter: string;
  orderBy: { id: string; desc: boolean }[];
}) => {
  return api
    .get<{ total: number; registers: any[] }>('maquinetas', {
      params: filters,
    })
    .then((res) => res.data);
};

export const findAllWithdrawers = (filter: {
  limit: number;
  page: number;
  filter: string;
  query?: Record<string, any>;
  initialDate?: string;
  finalDate?: string;
}) => {
  // POST /withdrawals/list carrega o objeto completo (incl. query.status) no body.
  return api
    .post<{ pages: number; registers: Withdrawal[] }>('withdrawals/list', filter)
    .then((res) => res.data);
};

export const updateWithdrawel = (
  id: string,
  data: {
    amount: number;
  },
) => {
  return api.put<Withdrawal>(`withdrawals/${id}`, data).then((res) => res.data);
};

export const findWithdrawel = (id: string) => {
  return api.get<Withdrawal>(`withdrawals/${id}`).then((res) => res.data);
};

export const getPendingWithdrawalsForApproval = () => {
  return api.get<Withdrawal[]>('withdrawals/pending-approvals').then((res) => res.data);
};

export const approveWithdrawal = (
  id: string,
  data: {
    status: 'APPROVED' | 'REJECTED';
    observation?: string;
  },
) => {
  return api
    .post<{
      message: string;
      status: string;
      approvals?: number;
      required?: number;
    }>(`withdrawals/${id}/approve`, data)
    .then((res) => res.data);
};

export const findAllMovements = (
  filter: {
    limit: number;
    customerId: string;
    page: number;
    maquinetaId?: string;
    filter: string;
  },
  exportPdf = false,
) => {
  return api
    .post<Orders>(
      'movements/all',
      {
        ...filter,
        exportPdf,
      },
      {
        responseType: exportPdf ? 'blob' : 'json',
      },
    )
    .then((res) => res.data);
};

export interface ChargebackDTO {
  id: string;
  movementId: string;
  status: 'OPEN' | 'IN_DISPUTE' | 'ACCEPTED' | 'REVERSED' | 'CANCELED';
  source: 'MANUAL' | 'PAGSEGURO_ONLINE' | 'PAGSEGURO_TERMINAL';
  reason: string;
  blockedAmount: string; // Decimal serialized as string — Number() before formatting
  chargebackDate: string | null;
  createdAt: string;
  movement: {
    id: string;
    transactionCode: string;
    amount: string; // Decimal as string
    orderAt: string;
    type: string;
    maquinetaId: string | null;
    status: string;
  };
  customer: { name: string };
}

export const findMyChargebacks = (filter: {
  page: number;
  limit: number;
  query?: { status?: string };
  initialDate?: string;
  finalDate?: string;
  [key: string]: unknown;
}) => {
  const { query, ...rest } = filter;
  const status = query?.status || undefined; // '' (placeholder) => omit => backend default = active
  return api
    .post<{ registers: ChargebackDTO[]; pages: number }>('chargebacks/mine', {
      ...rest,
      query: status ? { status } : undefined,
    })
    .then((res) => res.data);
};

export const findAllClients = (
  filter: {
    limit: number;
    page: number;
    maquinetaId?: string;
    filter: string;
  },
  exportPdf = false,
) => {
  return api
    .post(
      'clients/all',
      {
        ...filter,
        exportPdf,
      },
      {
        responseType: exportPdf ? 'blob' : 'json',
      },
    )
    .then((res) => res.data);
};

export const findAllRecurrences = async (
  filter: {
    limit: number;
    page: number;
    filter: string;
  },
  exportPdf = false,
) => {
  return api
    .post(
      'recurrences/all',
      {
        ...filter,
        exportPdf,
      },
      {
        responseType: exportPdf ? 'blob' : 'json',
      },
    )
    .then((res) => res.data);
};

export const findAllRecurrencesClients = async (filter: {
  limit: number;
  customerId: string;
  page: number;
  maquinetaId?: string;
  filter: string;
}) => {
  return api
    .get('recurrences/clients', {
      params: {
        ...filter,
      },
    })
    .then((res) => res.data);
};

export const findAllBalances = async (
  params: { limit: number; customerId?: string; page: number; filter: string },
  exportPdf: boolean,
) => {
  return api
    .get<{
      pages: number;
      registers: any[];
    }>('balances', {
      params,
      responseType: exportPdf ? 'blob' : 'json',
    })
    .then((res) => res.data);
};

export const findAllBalancesPending = (params: {
  limit: number;
  customerId?: string;
  page: number;
  filter: string;
}) => {
  return api
    .get<{
      pages: number;
      registers: any[];
    }>('balances-pending', {
      params,
    })
    .then((res) => res.data);
};

export const getBalancesTotalsByOrigin = async (params: {
  customerId?: string;
  initialDate?: string;
  finalDate?: string;
}) => {
  return api
    .get<{ key: string; total: number }[]>('balances/totals-by-origin', { params })
    .then((res) => res.data);
};

export const findReceivedFees = async (
  params: { limit: number; customerId?: string; page: number; filter?: string; initialDate?: string; finalDate?: string; originCustomerId?: string },
  exportPdf?: boolean,
) => {
  return api
    .get<{ pages: number; registers: any[] }>('balances/received-fees', {
      params,
      responseType: exportPdf ? 'blob' : 'json',
    })
    .then((res) => res.data);
};

export const getBalancesFeesSummary = async (params: {
  customerId?: string;
  initialDate?: string;
  finalDate?: string;
}) => {
  return api
    .get<{ total: number; byType: { type: string; total: number; count: number }[] }>(
      'balances/fees-summary',
      { params },
    )
    .then((res) => res.data);
};

export const getBalancesFeesBySeller = async (params: {
  limit: number;
  page: number;
  customerId?: string;
  initialDate?: string;
  finalDate?: string;
}) => {
  return api
    .get<{
      pages: number;
      registers: {
        originCustomerId: string;
        originName: string | null;
        total: number;
        count: number;
      }[];
    }>('balances/fees-by-seller', { params })
    .then((res) => res.data);
};

export const dashboardCustomer = async (filter: any) => {
  return api.get<DashboardResponse>(`dashboard/customer`, { params: filter }).then((res) => res.data);
};

export const deleteCustomerAccount = (id: string) => {
  return api.delete(`accounts/${id}`).then((res) => res.data);
};

export const deleteCustomer = async (authorizationId: string) => {
  return api
    .delete(`customers`, {
      headers: {
        'x-security-code': authorizationId,
      },
    })
    .then((res) => res.data);
};
export const accountFeatures = async () => {
  return api
    .get<{
      twoFaEnabled: boolean;
      masterPasswordEnabled: boolean;
      notificationsEnabled: boolean;
      emailsEnabled: boolean;
    }>(`customers/features`)
    .then((res) => res.data);
};

export const changeFeatureNotification = async (type: 'push' | 'email') => {
  return api.post(`customers/features`, { type }).then((res) => res.data);
};

export const setMasterPassword = async ({
  masterPassword,
  previousMasterPassword,
}: {
  masterPassword: string;
  previousMasterPassword?: string;
}) => {
  return api
    .post(`customers/master-password`, {
      masterPassword,
      previousMasterPassword,
    })
    .then((res) => res.data);
};

export const getCredentials = async (authorizationId: string) => {
  return api
    .get<{
      clientId: string;
      clientSecret: string;
    }>(`customers/credentials`, {
      headers: {
        'x-security-code': authorizationId,
      },
    })
    .then((res) => res.data);
};

export const createCredentials = () => {
  return api.post(`customers/credentials`).then((res) => res.data);
};

export const getAccounts = ({ page = 0, limit = 10, ...params }: any) => {
  return api.get(`accounts`, { params: { ...params, page, limit } }).then((res) => res.data);
};

export const toggleAccountFavorite = (id: string, isFavorite: boolean) => {
  return api.patch(`accounts/${id}/favorite`, { isFavorite }).then((res) => res.data);
};

export const getAntecipationValue = () => {
  return api.get<{ amount: number; amount_total: number }>(`balances-pending/antecipation`).then((res) => res.data);
};

export const getCharge = async (id: string) => {
  return api.get<Charge>(`charges/${id}`).then((res) => res.data);
};

export const findAllCharges = (
  filter: {
    limit: number;
    customerId: string;
    page: number;
    maquinetaId?: string;
    filter: string;
  },
  exportPdf = false,
) => {
  return api
    .post<Orders>(
      'charges/all',
      { ...filter, exportPdf },
      {
        responseType: exportPdf ? 'blob' : 'json',
      },
    )
    .then((res) => res.data);
};

export const getChargeFromPublic = async (id: string) => {
  return api.get<ChargePublic>(`public/charges/${id}`).then((res) => res.data);
};

export const getPublicPagSeguro = async (id: string) => {
  return api.post<{ public_key: string }>(`public/charges/${id}/pagseguro/key`).then((res) => res.data);
};

export const registerBilletForCharge = (id: string, recaptchaToken: string) => {
  return api.put<Charge>(`public/charges/${id}/register-billet`, { recaptchaToken }).then((res) => res.data);
};

export const generatePaymentsForCharge = async (id: string, type: 'pix' | 'billet' | 'instant') => {
  return api.post<Charge>(`charges/${id}/${type}`).then((res) => res.data);
};

export const generateInstantCharge = async (data: {
  amount: number;
  expiration?: number;
  deposit?: boolean;
}) => {
  return api.post<InstantQrCode>(`charges/instant`, data).then((res) => res.data);
};

export const processPaymentMercadoPago = (
  id: string,
  data: ICardPaymentFormData<ICardPaymentBrickPayer> & {
    recaptchaToken: string;
    description?: string;
    email: string;
    document: string;
    name: string;
  },
) => {
  return api.post<Charge>(`public/charges/${id}/mercado-pago`, data).then((res) => res.data);
};

export const payWithCreditCard = async (id: string, data: FormCardCreditCard & { recaptchaToken: string; autoRecurrence?: boolean }) => {
  return api.post<Charge>(`public/charges/${id}/credit-card`, data).then((res) => res.data);
};

export const payWithCreditCardRecharge = async (data: RechargeFormCardCreditCard & { recaptchaToken: string }) => {
  return api.post<Charge>(`charges/credit-card`, data).then((res) => res.data);
};

export const payWithCreditCardGetnet = async (id: string, data: FormCardCreditCard & { recaptchaToken: string }) => {
  return api.post<Charge>(`public/charges/${id}/getnet/credit-card`, data).then((res) => res.data);
};

export const log3dsFailure = async (
  chargeIdOrNull: string | null,
  data: { requestData: Record<string, any>; responseData?: Record<string, any>; errorMessage?: string },
) => {
  const url = chargeIdOrNull
    ? `public/charges/${chargeIdOrNull}/3ds-failure`
    : `public/3ds-failure`;
  return api.post(url, data).catch(() => {});
};

export const infoAccount = async () => {
  return api.get<CustomerInfo>(`customers/info`).then((res) => res.data);
};

export const findInstallmentsConfig = async () => {
  return api.get<InstallmentsConfig>(`charges/installmentsConfigRecharge`).then((res) => res.data);
};

export const findInstallmentsConfigCustom = async (paymentMethod: string) => {
  return api
    .get<InstallmentsConfig>(`charges/installmentsConfig?paymentMethod=${paymentMethod}`)
    .then((res) => res.data);
};

export const initializePaymentMercadoPago = (id: string) => {
  return api
    .get<{
      publicKey: string;
    }>(`public/charges/${id}/mercado-pago`)
    .then((res) => res.data);
};

export const estornarPix = (chargeId: string) => {
  return api.put(`charges/${chargeId}/pix/refund`).then((res) => res.data);
};
export const request2FaData = () => {
  return api.get<{ otpauth_url: string; base64: string }>('customers/twoFa').then((res) => res.data);
};

export const active2FaData = (token) => {
  return api.post('customers/twoFa', { token }).then((res) => res.data);
};

export const createInsurance = async (data: {
  holderId: string;
  startDate: string;
  modelId: string;
  endDate: string;
}) => {
  return api.post(`insurances`, data).then((res) => res.data);
};

export const createHolder = async (data: CreateHolderForm) => {
  return api.post(`insurances/holders`, data).then((res) => res.data);
};

export const findAllHolders = ({
  limit = 20,
  filter = '',
  page = 0,
}: {
  limit?: number;
  page?: number;
  filter?: string;
}) => {
  return api
    .get<CustomerListResponse>('insurances/holders', {
      params: {
        limit,
        page,
        filter,
      },
    })
    .then((res) => res.data);
};

export const findAllInsurancesModels = ({
  limit = 20,
  filter = '',
  page = 0,
}: {
  limit?: number;
  page?: number;
  filter?: string;
}) => {
  return api
    .get<CustomerListResponse>('insurances/models', {
      params: {
        limit,
        page,
        filter,
      },
    })
    .then((res) => res.data);
};

export const findAllInsurances = ({
  limit,
  page,
  companyId,
  filter,
}: {
  limit: number;
  companyId: string;
  page: number;
  filter: string;
}) => {
  return api
    .get<Orders>('insurances', {
      params: { filter, limit, page, companyId },
    })
    .then((res) => res.data);
};
export const cancelInsurance = async ({ id }: { id: string }) => {
  return api.delete(`insurances/${id}`).then((res) => res.data);
};

export const findCards = async ({ limit = 10, page = 0 }: { limit: number; page: number } | undefined) => {
  return api.post<CardList>(`cards/all`, { limit, page }).then((res) => res.data);
};
export const findAllMovementsCard = ({
  limit = 20,
  filter = '',
  page = 0,
  companyId,
  customerId,
  cardId,
}: {
  limit?: number;
  page?: number;
  filter?: string;
  companyId?: string;
  customerId?: string;
  cardId: string;
}) => {
  return api
    .get<any>(`cards/${cardId}/movements`, {
      params: {
        limit: +limit,
        page: +page,
        companyId,
        customerId,
        filter,
      },
    })
    .then((res) => res.data);
};

export interface CardBrandOption {
  label: string;
  value: string;
}
export const findCardBrands = async () => {
  return api.get<CardBrandOption[]>(`cards/brands`).then((res) => res.data);
};

export const startOnboarding = async (data: {
  captchaToken?: string;
  turnstileToken?: string;
  type: 'PF' | 'PJ';
}) => {
  return api
    .post<{
      id: string;
    }>('onboarding-customer', data)
    .then((res) => res.data);
};

export type NextOnboarding = {
  step: 'credentials' | 'company' | 'personal' | 'address' | 'documents' | 'selfie' | 'complete';
  [key: string]: any;
};

export const nextOnboarding = async (id: string, data: NextOnboarding) => {
  return api
    .put<{
      id: string;
    }>(`onboarding-customer/${id}`, data)
    .then((res) => res.data);
};

export const findOnboarding = async (id: string) => {
  return api.get<Onboarding>(`onboarding-customer/${id}`).then((res) => res.data);
};

export const findAllRecurrencyLinks = async (filter: { limit: number; page: number; filter: string }) => {
  return api.post<RecurrencyLink>(`recurrency-links/all`, filter).then((res) => res.data);
};

export const createRecurrencyLink = async (data: {
  title: string;
  description: string;
  image: string;
  itens: { planId: string }[];
}) => {
  return api.post<RecurrencyLink>(`recurrency-links`, data).then((res) => res.data);
};

export const updateRecurrencyLink = async (id: string, data: { title: string; description: string }) => {
  return api.put<RecurrencyLink>(`recurrency-links/${id}`, data).then((res) => res.data);
};

export const findRecurrencyLink = async (id: string) => {
  return api.get<RecurrencyLinkItemGet>(`recurrency-links/${id}`).then((res) => res.data);
};

export const findRecurrencyLinkPublic = async (id: string) => {
  return api.get<RecurrencyLinkItemGet>(`recurrency-links/public/${id}`).then((res) => res.data);
};

export const addPlanToRecurrencyLink = async (id: string, data: { planId: string }) => {
  return api.post<RecurrencyLink>(`recurrency-links/${id}/item`, data).then((res) => res.data);
};

export const removePlanFromRecurrencyLink = async (id: string, data: { planId: string }) => {
  return api.delete<RecurrencyLink>(`recurrency-links/${id}/item/${data.planId}`).then((res) => res.data);
};

export const updateRecurrency = (id: string, data: any) => {
  return api.put(`/recurrences/${id}`, data);
};

export const generateRecurrencyLink = async (
  id: string,
  data: { name: string; email: string; document: string; reffererId?: string; phone: string; coupon?: string },
) => {
  return api
    .post<{
      link: string;
    }>(`recurrency-links/${id}/generate`, data)
    .then((res) => res.data);
};

export const findTerminalsApp = (filters: {
  limit: number;
  page: number;
  filter: string;
  orderBy: { id: string; desc: boolean }[];
}) => {
  return api
    .get<{ total: number; registers: any[] }>('terminals', {
      params: filters,
    })
    .then((res) => res.data);
};

export const deleteTerminal = async (id: string) => {
  return api.delete(`maquinetas/${id}`).then((res) => res.data);
};

export const createTerminal = async (data: { name: string; email: string; document: string; phone?: string }) => {
  return api.post(`terminals`, data).then((res) => res.data);
};

export const editTerminal = async (
  id: string,
  data: {
    name: string;
    email: string;
    document: string;
    phone?: string;
  },
) => {
  return api.put(`terminals/${id}`, data).then((res) => res.data);
};

export const findSharedRecurrencyLink = async (id: string) => {
  return api
    .get<{
      url: string;
    }>(`recurrency-links/shared/${id}`)
    .then((res) => res.data);
};

export const generateRecurrenceReport = async (id: string) => {
  return api
    .get<{
      url: string;
    }>(`recurrency-links/${id}/report`, {
      responseType: 'blob',
    })
    .then((res) => res.data);
};

export const findDispute = async (id: string) => {
  return api.get<Dispute>(`disputes/public/${id}`).then((res) => res.data);
};

export const sendMessageDispute = async (id: string, data: { message: string }) => {
  return api.post<Dispute>(`disputes/public/${id}/message`, data).then((res) => res.data);
};

export const sendFileDispute = async (id: string, data: { file?: File }) => {
  const formData = new FormData();
  if (data.file) {
    formData.append('file', data.file);
  }
  return api
    .post<Dispute>(`disputes/public/${id}/file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((res) => res.data);
};

export const saveAccountPixDefault = async (data: { pixKey: string; document: string; name: string }) => {
  return api.post(`customers/pix-default`, data).then((res) => res.data);
};

export const getAccountPixDefault = async () => {
  return api.get(`customers/pix-default`).then((res) => res.data);
};

export const validateCouponApi = async (planId: string, coupon: string) => {
  return api
    .post<{
      coupon: string;
      discount: number;
    }>(`recurrency-links/${planId}/coupon`, { coupon })
    .then((res) => res.data);
};

// Account Access Management
export interface CustomerSearchResult {
  id: string;
  name: string;
  email: string;
  accountNumber: string;
  document_number: string;
}

export interface AccountAccess {
  id: string;
  customerAccessId: string;
  validTo: string;
  isMaster: boolean;
  customerAccess: {
    id: string;
    name: string;
    email: string;
    accountNumber: string;
  };
}

export const searchCustomerByAccount = async (accountNumber: string) => {
  return api.get<CustomerSearchResult>(`customers/search/account/${accountNumber}`).then((res) => res.data);
};

export const getMyAccountAccess = async (customerId: string) => {
  return api.get<AccountAccess[]>(`customers/${customerId}/access`).then((res) => res.data);
};

export const addMyAccountAccess = async (
  customerId: string,
  data: { customerAccessId: string; validTo: string; isMaster: boolean },
) => {
  return api.post<AccountAccess>(`customers/${customerId}/access`, data).then((res) => res.data);
};

export const removeMyAccountAccess = async (customerId: string, accessId: string) => {
  return api.delete(`customers/${customerId}/access/${accessId}`).then((res) => res.data);
};

export const getScheduledWithdrawTimes = async (): Promise<string[]> => {
  const { data } = await api.get('/platform-settings/scheduled-withdraw-times')
  return data
}

// Charge Batches
export type ChargeBatchStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'PARTIALLY_COMPLETED' | 'FAILED';
export type ChargeBatchItemStatus = 'PENDING' | 'PROCESSING' | 'SUCCEEDED' | 'FAILED' | 'SKIPPED_DUPLICATE';

export interface ChargeBatch {
  id: string;
  status: ChargeBatchStatus;
  totalItems: number;
  processedItems: number;
  succeededItems: number;
  failedItems: number;
  callbackUrl?: string | null;
  createdAt: string;
  startedAt?: string | null;
  finishedAt?: string | null;
}

export interface ChargeBatchItem {
  id: string;
  index: number;
  externalId: string;
  status: ChargeBatchItemStatus;
  chargeId?: string | null;
  error?: { code?: string | number; message?: string } | null;
  attempts: number;
  processedAt?: string | null;
}

// Flags de feature da empresa do usuário logado (mapa key->boolean), para gatear menu/UI.
export const getMyFeatureFlags = () => {
  return api.get<Record<string, boolean>>('feature-flags/my').then((res) => res.data);
};

// Paginação no padrão do app: page 0-based + limit; o backend já devolve { registers, pages, total }.
export const findChargeBatches = (filter: {
  limit: number;
  page: number;
  filter?: string;
  status?: string;
}) => {
  return api
    .get<{ registers: ChargeBatch[]; total: number; pages: number }>('charges/batch', {
      params: {
        status: filter.status || undefined,
        page: filter.page,
        limit: filter.limit,
      },
    })
    .then((res) => res.data);
};

export const getChargeBatch = (id: string) => {
  return api.get<ChargeBatch>(`charges/batch/${id}`).then((res) => res.data);
};

export const findChargeBatchItems = (
  id: string,
  filter: { limit: number; page: number; status?: string },
) => {
  return api
    .get<{ registers: ChargeBatchItem[]; total: number; pages: number }>(
      `charges/batch/${id}/items`,
      {
        params: {
          status: filter.status || undefined,
          page: filter.page,
          limit: filter.limit,
        },
      },
    )
    .then((res) => res.data);
};

export const retryChargeBatch = (id: string) => {
  return api.post<{ batchId: string; requeued: number }>(`charges/batch/${id}/retry`).then((res) => res.data);
};

// ===== WITHDRAWAL BATCHES =====
export type WithdrawalBatchStatus =
  | 'PENDING'
  | 'PENDING_APPROVAL'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'PARTIALLY_COMPLETED'
  | 'FAILED'
  | 'CANCELED'

export type WithdrawalBatchItemStatus = 'PENDING' | 'PROCESSING' | 'SUCCEEDED' | 'FAILED'

export interface WithdrawalBatch {
  id: string
  status: WithdrawalBatchStatus
  fileName?: string | null
  totalItems: number
  totalAmount: string
  processedItems: number
  succeededItems: number
  failedItems: number
  createdAt: string
  startedAt?: string | null
  finishedAt?: string | null
}

export interface WithdrawalBatchItem {
  id: string
  index: number
  externalId: string
  name?: string | null
  document?: string | null
  pixKey: string
  pixKeyType?: string | null
  description?: string | null
  amount: string
  status: WithdrawalBatchItemStatus
  withdrawalId?: string | null
  error?: { code?: string | number; message?: string } | null
}

export interface BatchItemPayload {
  externalId: string
  pixKey: string
  amount: number
  name?: string
  document?: string
  pixKeyType?: string
  description?: string
}

export const findWithdrawalBatches = (filter: { limit: number; page: number; status?: string }) => {
  return api
    .get<{ registers: WithdrawalBatch[]; total: number; pages: number }>('withdrawals/batch', {
      params: { status: filter.status || undefined, page: filter.page, limit: filter.limit },
    })
    .then((res) => res.data)
}

export const getWithdrawalBatch = (id: string) => {
  return api.get<WithdrawalBatch>(`withdrawals/batch/${id}`).then((res) => res.data)
}

export const findWithdrawalBatchItems = (id: string, filter: { limit: number; page: number; status?: string }) => {
  return api
    .get<{ registers: WithdrawalBatchItem[]; total: number; pages: number }>(`withdrawals/batch/${id}/items`, {
      params: { status: filter.status || undefined, page: filter.page, limit: filter.limit },
    })
    .then((res) => res.data)
}

export const createWithdrawalBatch = (data: { items: BatchItemPayload[]; fileName?: string }) => {
  return api
    .post<{ batchId: string; status: WithdrawalBatchStatus; totalItems: number }>('withdrawals/batch', data)
    .then((res) => res.data)
}

export const addWithdrawalBatchItem = (id: string, data: BatchItemPayload) => {
  return api.post<WithdrawalBatchItem>(`withdrawals/batch/${id}/items`, data).then((res) => res.data)
}

export const updateWithdrawalBatchItem = (id: string, itemId: string, data: Partial<BatchItemPayload>) => {
  return api.put<WithdrawalBatchItem>(`withdrawals/batch/${id}/items/${itemId}`, data).then((res) => res.data)
}

export const deleteWithdrawalBatchItem = (id: string, itemId: string) => {
  return api.delete(`withdrawals/batch/${id}/items/${itemId}`).then((res) => res.data)
}

export const confirmWithdrawalBatch = (id: string, authorizationId: string) => {
  return api
    .post<{ batchId: string; status: WithdrawalBatchStatus }>(`withdrawals/batch/${id}/confirm`, {}, {
      headers: { 'x-security-code': authorizationId },
    })
    .then((res) => res.data)
}

export const approveWithdrawalBatch = (id: string) => {
  return api.post(`withdrawals/batch/${id}/approve`).then((res) => res.data)
}

export const rejectWithdrawalBatch = (id: string) => {
  return api.post(`withdrawals/batch/${id}/reject`).then((res) => res.data)
}

export const retryWithdrawalBatch = (id: string) => {
  return api.post<{ batchId: string; requeued: number }>(`withdrawals/batch/${id}/retry`).then((res) => res.data)
}

export const deleteWithdrawalBatch = (id: string) => {
  return api.delete(`withdrawals/batch/${id}`).then((res) => res.data)
}

export const createFeedback = async (data: { type: string; message: string; phone?: string }) => {
  return api.post('feedbacks', data).then((res) => res.data);
};

export default api;
